/**
 * @module components/TableFieldSetting/FieldColumnSorting
 * @description 表格图表 — 字段列排序拖拽
 *
 * 维/指列表使用 useQueryColumnDimensions / useQueryColumnMetrics，
 * 以便 QB 字段重命名后拖拽列表中的展示名与 column_sort 联动保持一致。
 * 订阅策略见 hooks/dva/useQueryColumnLists.ts（数组引用相等，非 shallowEqual）。
 */

import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import ConfigCollapseCard from '@/pages/datalab/components/ConfigCollapseCard';
import { QuickDragList } from '@/pages/datalab/components/QuickDragList';
import type { IQuickDragItem } from '@/pages/datalab/components/QuickDragList/types';
import LabelText from '@/pages/datalab/components/LabelText';
import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import { COLUMN_DISPLAY_TEXT } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/constants';
import { CSActionType } from '../../../types';
import {
  useChartSettingsDispatch,
  useChartSettingsSelector,
} from '../../../context';
import {
  useQueryColumnDimensions,
  useQueryColumnMetrics,
  useQuerySelector,
} from '../../../hooks/deps';
import { getEffectiveColumnSort } from './getEffective';
import {
  getColumnSortItemId,
  isColumnSortDimension,
} from '../../../linkage/qbFields/qbLinkage/handlers/columnSort/untils/keys';
import { detectCompareTime } from '../../../linkage/qbFields/qbLinkage/detectChanges';
import { EMPTY_COLUMN_INFO_LIST } from '../../../utils/constants';
import { mapQuickDragItemsToColumnSort, selectFieldColumnSortingSettings } from './untils';
import type { IColumnPercentageItem } from '../../../linkage/qbFields/qbLinkage/handlers/columnSort/untils/columnPercentage';
import type { IDimensionPercentageConfig } from '../../../types/settings/dimensionPercentage';
import '../index.scss';

const DragListTitle: React.FC = () => {
  const { formatMessage } = useIntl();
  return (
    <div className="dbl-field-sort-drag-title">
      <div className="dbl-field-sort-drag-title-item">
        <span className="dbl-field-sort-drag-title-icon dimension" />
        <LabelText preset="838697-12-400">{formatMessage({ id: 'dbl_personal_data_dimension' })}</LabelText>
      </div>
      <div className="dbl-field-sort-drag-title-item">
        <span className="dbl-field-sort-drag-title-icon metric" />
        <LabelText preset="838697-12-400">{formatMessage({ id: 'dbl_personal_data_metric' })}</LabelText>
      </div>
    </div>
  );
};

const FieldColumnSorting: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const dispatch = useChartSettingsDispatch();

  // 展示名 / column_name 变更须用专用 hook（见模块顶注释）
  const columnDimensions = useQueryColumnDimensions();
  const columnMetrics = useQueryColumnMetrics();
  const columnFilters = useQuerySelector(
    s => s.columnFilters ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnUserFilters = useQuerySelector(
    s => s.columnUserFilters ?? EMPTY_COLUMN_INFO_LIST,
  );
  const hasUserFilters = useQuerySelector(s => !!s.hasUserFilters);

  const settingsSlice = useChartSettingsSelector(
    selectFieldColumnSortingSettings,
  );

  const columnSort = useMemo(() => {
    const compareTime = detectCompareTime(columnFilters);
    return getEffectiveColumnSort(settingsSlice, {
      columnDimensions,
      columnMetrics,
      columnFilters,
      columnUserFilters,
      hasUserFilters,
      compareTime,
    });
  }, [
    settingsSlice,
    columnDimensions,
    columnMetrics,
    columnFilters,
    columnUserFilters,
    hasUserFilters,
  ]);

  const dragListTitle = useMemo(() => <DragListTitle />, [locale]);

  const options = useMemo(() => {
    const isEn = locale === LANGUAGE_EN;
    return columnSort?.map((column: IColumnSortItem) => {
      const isDimension = isColumnSortDimension(column);
      const isMetric = !isDimension;
      let text = '';
      if (column?.is_compare_date) {
        text = isEn ? COLUMN_DISPLAY_TEXT.COMPARE_TIME.EN : COLUMN_DISPLAY_TEXT.COMPARE_TIME.CN;
      } else {
        text = isEn ? column.column_display_en : column.column_display_cn;
      }
      return {
        id: getColumnSortItemId(column),
        text,
        source: column,
        className: classNames('dbl-field-column-sort-item', { 'dbl-field-column-sort-item--dimension': isDimension, 'dbl-field-column-sort-item--metric': isMetric }),
        hoverClassName: 'dbl-field-column-sort-item-hover',
        activeClassName: classNames({ 'dbl-field-column-sort-item--dimension-active': isDimension, 'dbl-field-column-sort-item--metric-active': isMetric }),
      };
    }) || [];
  }, [columnSort, locale]);

  const handleChange = useCallback((items: IQuickDragItem[]) => {
    const columnPercentageRaw =
      settingsSlice.draft.column_percentage ?? settingsSlice.base.column_percentage;
    const columnPercentage = Array.isArray(columnPercentageRaw)
      ? (columnPercentageRaw as IColumnPercentageItem[])
      : undefined;
    const dimensionPercentageRaw =
      settingsSlice.draft.dimension_percentage ??
      settingsSlice.base.dimension_percentage;
    const dimensionPercentage =
      dimensionPercentageRaw &&
      typeof dimensionPercentageRaw === 'object' &&
      !Array.isArray(dimensionPercentageRaw)
        ? (dimensionPercentageRaw as IDimensionPercentageConfig)
        : undefined;

    const newFieldSort = mapQuickDragItemsToColumnSort(items, {
      columnPercentage,
      dimensionPercentage,
    });
    if (!newFieldSort) {
      return;
    }
    dispatch({
      type: CSActionType.UPDATE_DRAFT,
      payload: { column_sort: newFieldSort },
    });
  }, [dispatch, settingsSlice]);

  return (
    <ConfigCollapseCard title={formatMessage({ id: 'dbl_chart_field_column_sorting' })} className="dbl-config-collapse-card-h2 dbl-field-column-sort">
      <QuickDragList title={dragListTitle} value={options ?? []} onChange={handleChange} className="dbl-field-column-sort-drag-list" maxHeight={466} />
    </ConfigCollapseCard>
  );
};

export default React.memo(FieldColumnSorting);
